// © Microsoft Corporation. All rights reserved.

import {History, LocationDescriptor, LocationDescriptorObject, createBrowserHistory, createMemoryHistory} from "history";

import { canUseDOM } from "@msnews/isomorphic-util";

import LocationState = History.LocationState;

type CreateHistory<O, H> = (options?: O) => History & H;

/**
 * Function to preserve query parameters for the current location, preventing react router from overriding the query params
 * @param history current History object
 * @param queryParameters array of query params to preserve
 * @param location current location object
 */
export function preserveQueryParameters(history: History, queryParameters: string[], location: LocationDescriptorObject): LocationDescriptorObject {
    const currentQuery = new URLSearchParams(history.location.search);
    if (currentQuery) {
        const preservedQuery = new URLSearchParams();
        const locationPreservedQuery = location.search ? new URLSearchParams(location.search): null;
        for (const queryParamKey of queryParameters) {
            let queryParamValue = currentQuery.getAll(queryParamKey);
            if (locationPreservedQuery && locationPreservedQuery.getAll(queryParamKey).length) {
                queryParamValue = locationPreservedQuery.getAll(queryParamKey);
            }
            if (!queryParamValue.length) {
                continue;
            }
            queryParamValue.forEach(value => preservedQuery.append(queryParamKey, value));
        }
        let preservedQueryParams = "";
        preservedQuery.forEach((value, key) => preservedQueryParams = preservedQueryParams.concat(`&${key}=${value}`));
        location.search = preservedQueryParams.slice(1);
    }
    return location;
}

/**
 * Function to create the location object
 * @param location current location object
 * @param state location state, encompasses any information about the location that isn't shown in the URL
 * @param queryParams query params to be used in the location object
 */
export function createLocationDescriptorObject(location: LocationDescriptor, state?: LocationState, queryParams?: string): LocationDescriptorObject {
    return typeof location === "string" ? {pathname: location, state, search: queryParams} : location;
}

/**
 * Function to create a custom history where the query params are preserved. This prevents React Router from overriding the query params
 * when the route updates
 * @param createHistory history create method to be used
 * @param queryParameters query parameters that should be preserved
 */
export function createPreserveQueryHistory<O, H>(createHistory: CreateHistory<O, H>, queryParameters: string[]): any {
    const history = createHistory();
    const oldPush = history.push;
    const oldReplace = history.replace;
    history.push = (path: LocationDescriptor, state?: LocationState, queryParams?: string) =>
        oldPush.apply(history, [preserveQueryParameters(history, queryParameters, createLocationDescriptorObject(path, state, queryParams))]);
    history.replace = (path: LocationDescriptor, state?: LocationState, queryParams?: string) =>
        oldReplace.apply(history, [preserveQueryParameters(history, queryParameters, createLocationDescriptorObject(path, state, queryParams))]);
    return history;
}

/**
 * List of query params that the router should preserve
 */
const preservedQueryParams = [
    "locale",
    "token",
    "returnTo",
    "item",
    "pcsonly",
    "layout",
    "ocid",
    "isGitConfig"
];

// PeregrineHistory is client side only, null on server side
const PeregrineHistory = canUseDOM() ? createPreserveQueryHistory(createBrowserHistory, preservedQueryParams) : null;

export default PeregrineHistory;

/**
 * Location context object, used to store the last visited route and other useful information
 */
export interface LocationContext {
    /** Route path of the last visited route */
    previousPath: string;

    /** Context used for experience for the route */
    context?: any;
}
